// src/scenes/login/index.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Alert,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff, LockOutlined } from "@mui/icons-material";
import { useLoginMutation } from "state/api";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { login as loginAction } from "state/authSlice";

const decodeJwt = (token) => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const json = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );
  return JSON.parse(json);
};

const LoginPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loginRequest, { isLoading }] = useLoginMutation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // ✅ Always show generic branding on login page
  const brandTitle = "CENTRA for Chamaas";

  // ✅ Optional but recommended: clear group branding on login screen
  useEffect(() => {
    localStorage.removeItem("groupName");
    localStorage.removeItem("groupId");
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    try {
      const result = await loginRequest({ email, password }).unwrap();

      localStorage.setItem("token", result.token);
      localStorage.setItem("requiresPasswordChange", String(!!result.changepassword));

      let computedRoleGroupId = null;

      try {
        const payload = decodeJwt(result.token);

        const roleGroupId = payload.RoleGroupId ?? payload.roleGroupId;
        computedRoleGroupId = roleGroupId;

        const userId = payload.Id ?? payload.id ?? payload.sub;
        const groupId = payload.GroupId ?? payload.groupId;

        // ✅ group name claim from backend: new Claim("Group", ...)
        const groupName =
          payload.Group ?? payload.group ?? payload.GroupName ?? payload.groupName;

        if (roleGroupId != null) localStorage.setItem("roleGroupId", String(roleGroupId));
        if (userId != null) localStorage.setItem("userId", String(userId));
        if (groupId != null) localStorage.setItem("groupId", String(groupId));
        if (groupName) localStorage.setItem("groupName", String(groupName));

        if (payload.email) localStorage.setItem("email", payload.email);
        if (payload.Username ?? payload.unique_name) {
          localStorage.setItem("username", String(payload.Username ?? payload.unique_name));
        }

        const firstName = payload.FirstName ?? payload.firstName;
        const lastName = payload.LastName ?? payload.lastName;
        const fullName =
          payload.Name ?? payload.name ?? [firstName, lastName].filter(Boolean).join(" ");
        if (fullName) localStorage.setItem("fullName", String(fullName));
      } catch (e2) {
        console.warn("JWT decode failed", e2);
      }

      dispatch(
        loginAction({ token: result.token, requiresPasswordChange: !!result.changepassword })
      );

      if (result.changepassword === true) {
        navigate("/force-password-change", { replace: true });
        return;
      }

      // ✅ Redirect sys admin to Admin Switch screen
      if (String(computedRoleGroupId) === "1") {
        navigate("/admin-switch", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      console.error("Login error:", err);
      setErrorMsg("Login failed. Check your email and password.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.alt} 60%, ${theme.palette.background.default} 100%)`,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1080,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
          gap: 3,
          alignItems: "stretch",
        }}
      >
        <Card
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
            backgroundColor: theme.palette.background.alt,
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  display: "grid",
                  placeItems: "center",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                }}
              >
                <LockOutlined />
              </Box>

              <Box>
                <Typography variant="h4" fontWeight={800} lineHeight={1.1}>
                  {brandTitle}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.secondary[200], mt: 0.5 }}>
                  Track contributions • Loans • Fines • Guarantees — in one place.
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2.5, opacity: 0.35 }} />

            <Typography variant="h6" fontWeight={700} gutterBottom>
              Why Centra?
            </Typography>

            <Box sx={{ display: "grid", gap: 1 }}>
              <Typography sx={{ color: theme.palette.secondary[100] }}>
                ✅ Transparent member schedules and balances
              </Typography>
              <Typography sx={{ color: theme.palette.secondary[100] }}>
                ✅ Smart loan tracking and repayments
              </Typography>
              <Typography sx={{ color: theme.palette.secondary[100] }}>
                ✅ Fine automation and accountability
              </Typography>
              <Typography sx={{ color: theme.palette.secondary[100] }}>
                ✅ Group-level reporting for leadership
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Typography variant="h5" fontWeight={800} mb={0.5}>
              Sign in
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.secondary[200], mb: 3 }}>
              Use your email and password to access your chamaa workspace.
            </Typography>

            {errorMsg ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMsg}
              </Alert>
            ) : null}

            <Box component="form" onSubmit={handleLogin} sx={{ display: "grid", gap: 2 }}>
              <TextField
                label="Email"
                type="email"
                value={email}
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ mt: 1, borderRadius: 2, py: 1.2, fontWeight: 800 }}
              >
                {isLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={18} />
                    Signing in...
                  </Box>
                ) : (
                  "Login"
                )}
              </Button>

              <Button
                type="button"
                variant="text"
                sx={{ textTransform: "none", justifySelf: "start", px: 0 }}
                onClick={() => alert("Hook this to your reset flow when ready.")}
              >
                Forgot password?
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LoginPage;
